import { Box, Checkbox, Stack, Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { cloneElement, CSSProperties, forwardRef, Ref, useEffect, useRef } from 'react';
import { VariableSizeList } from 'react-window';

import { HBSelectMultiColumnColumnDefs } from '../HBSelectMultiColumn';

export interface ListboxProps {
  pageSize: number;
  columnDefs: HBSelectMultiColumnColumnDefs<any>[];
  items: any[];
  totalItems?: number;
  loadNextPage?: () => void;
  role: any;
  children: any;
  inputValue: string;
  rowHeight?: number;
  height?: number;
  style: CSSProperties;
  setScrollToIndex: (startIndex: number) => void;
  scrollToIndex: number;
  value: any[];
  multiple: boolean;
}

const Listbox = forwardRef(function Listbox(props: ListboxProps, ref: Ref<any>) {
  const breakpointMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const {
    children,
    role,
    height,
    rowHeight,
    pageSize,
    items,
    loadNextPage,
    inputValue,
    columnDefs,
    totalItems,
    setScrollToIndex,
    scrollToIndex,
    value,
    multiple,
    ...other
  } = props;

  const CHECKBOX_WIDTH = 40;
  const HORIZONTAL_PADDING = 32;

  const listRef = useRef<VariableSizeList>(null);
  const sizeMap = useRef<Record<number, number>>({});

  useEffect(() => {
    if (items.length > 0) {
      if (pageSize > items.length) {
        const index = (items?.length || pageSize + 1) - pageSize;
        if (items.length > scrollToIndex) {
          setScrollToIndex(index);
        }
      }
    }
  }, [items]);

  const setSize = (index: number, size: number) => {
    if (sizeMap.current[index] !== size) {
      sizeMap.current[index] = size;
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    }
  };

  const getSize = (index: number) => {
    return sizeMap.current[index] || rowHeight || 40;
  };

  const onItemsRendered = ({ visibleStartIndex, visibleStopIndex }: any) => {
    if (visibleStopIndex >= items.length - 1 && (totalItems ?? 0) > items.length) {
      setScrollToIndex(visibleStartIndex);
      loadNextPage?.();
    }
  };

  const rowRenderer = ({
    key,
    index,
    style,
  }: {
    key: string;
    index: number;
    style: CSSProperties;
  }) => {
    const isChecked = (index: number) => {
      const idField = columnDefs.filter((col) => col.isIdField)[0]?.field;
      if (multiple) {
        return (
          items[index] && value?.findIndex((item) => item[idField] === items[index][idField]) > -1
        );
      } else {
        return (
          items[index] && value && value[idField as keyof typeof value] === items[index][idField]
        );
      }
    };

    return (
      children[index] &&
      cloneElement(children[index], {
        style,
        children: (
          <Box key={key} style={props.style} sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row-reverse',
                width: '100%',
              }}
            >
              {multiple && (
                <Stack
                  sx={{
                    display: 'flex',
                    width: 40,
                  }}
                  component={'div'}
                >
                  <Checkbox checked={isChecked(index)} />
                </Stack>
              )}
              {columnDefs
                .filter((col) => !col.hidden)
                .map((col) => {
                  if (col.cellRenderer) {
                    return (
                      <Stack
                        sx={{
                          display: 'flex',
                          width: col.width,
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: col.width,
                        }}
                        component={'div'}
                      >
                        {col.cellRenderer(items[index])}
                      </Stack>
                    );
                  }
                  return (
                    <Stack
                      sx={{
                        display: 'flex',
                        width: col.width,
                        minWidth: col.width,
                        flexDirection: 'row-reverse',
                      }}
                      component={'div'}
                    >
                      <Tooltip title={items[index] && items[index][col.field]} arrow>
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            direction: 'ltr',
                          }}
                        >
                          {items[index] && items[index][col.field]}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  );
                })}
            </Box>
          </Box>
        ),
      })
    );
  };

  return (
    <Box ref={ref}>
      <Box
        {...other}
        sx={{
          width: breakpointMdUp
            ? '100%'
            : Number(
                columnDefs
                  .filter((column) => !column.isIdField)
                  .map((column) => column.width)
                  .reduce((a, b) => Number(a) + Number(b)) || 0,
              ) +
              HORIZONTAL_PADDING +
              (props.multiple ? CHECKBOX_WIDTH : 0),
        }}
      >
        <VariableSizeList
          ref={listRef}
          height={height || 200}
          width="100%"
          itemCount={totalItems ?? 0}
          itemSize={getSize}
          onItemsRendered={onItemsRendered}
          overscanCount={2}
          style={{ width: '100%', maxWidth: '100%' }}
        >
          {rowRenderer(children)}
        </VariableSizeList>
      </Box>
    </Box>
  );
});

export default Listbox;
